
import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import LicensePlateSelector from './LicensePlateSelector';
import LicensePlateInput from './LicensePlateInput';
import { plateFormats } from '../utils/plateFormats';
import SearchResults from './SearchResults';
import { getClientInfo } from '../utils/client';
import GdprCheckbox from './GdprCheckbox';
import './SearchForm.css';

const RECAPTCHA_SITE_KEY_V3 = process.env.REACT_APP_RECAPTCHA_SITE_KEY_V3; // állítsd be .env-ben
const RECAPTCHA_SITE_KEY_V2 = process.env.REACT_APP_RECAPTCHA_SITE_KEY_V2; // állítsd be .env-ben

const SearchForm = ({ onSearch }) => {
  const [plateNumber, setPlateNumber] = useState('');
  const [email, setEmail] = useState('');
  const [nickname, setNickname] = useState('');
  const [format, setFormat] = useState(plateFormats.find(f => f.id === 'classic'));
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [buttonLabel, setButtonLabel] = useState('Keresés');

  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [relatedNicknames, setRelatedNicknames] = useState([]);
  const [isTaxi, setIsTaxi] = useState(false);
  const [isElectric, setIsElectric] = useState(false);
  const [gdprChecked, setGdprChecked] = useState(false);

  const nicknameRef = useRef(null);

  // --- RECAPTCHA FALLBACK STATE-EK ---
  const [showV2Fallback, setShowV2Fallback] = useState(false);
  const [v2Token, setV2Token] = useState(null);

  const handleFormatChange = (selectedFormat) => {
    setFormat(selectedFormat);
  };

  const validateClientSide = () => {
    if (!plateNumber || !nickname || !format) {
      return 'Kérlek, tölts ki minden mezőt és válassz formátumot!';
    }
    const pattern = format.regex;
    if (!pattern.test(plateNumber)) {
      return `A rendszám nem felel meg a(z) ${format.placeholder} formátumnak.`;
    }
    if (nickname.trim().length < 2 || nickname.trim().length > 50) {
      return 'A becenév 2–50 karakter legyen.';
    }
    return '';
  };

  // v3 Token beszerzése gombnyomáskor
  const getV3Token = async () => {
    if (!window.grecaptcha || !window.grecaptcha.execute) {
      throw new Error('reCAPTCHA v3 nincs betöltve');
    }
    return await window.grecaptcha.execute(RECAPTCHA_SITE_KEY_V3, { action: 'submit_data' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // 0. GDPR elfogadás ellenőrzése
    if (!gdprChecked) return;

    // 1. Kliens oldali "gyors" validáció (UX védelem)
    const clientErr = validateClientSide();
    if (clientErr) { setError(clientErr); return; }

    setLoading(true);
    setError('');

    try {
      let captchaToken = null;
      let captchaVersion = 'v3';

      if (showV2Fallback) {
        captchaToken = v2Token;
        captchaVersion = 'v2';
      } else {
        try {
          captchaToken = await getV3Token();
        } catch (v3Err) {
          console.warn('reCAPTCHA v3 sikertelen, átváltás v2 fallbackre:', v3Err);
          setShowV2Fallback(true);
          setLoading(false);
          setError('Biztonsági ellenőrzés szükséges. Kérjük, pipáld ki az alábbi dobozt!');
          return;
        }
      }

      const response = await fetch('/api/submit-data', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plateNumber,
          nickname,
          format,
          isTaxi,
          isElectric,
          email,
          gdprAccepted: gdprChecked,
          recaptchaToken:captchaToken,
          recaptchaVersion: captchaVersion,
          client: getClientInfo() // Segédfüggvény az adatoknak
        })
      });

      const result = await response.json();

      // 4. Ha a backend elutasítja a v3-at (alacsony score / 403 status), aktiváljuk a v2-t
      if (!response.ok && (result.requireV2 || response.status === 403)) {
        setShowV2Fallback(true);
        setError('Kérjük, igazold magad a biztonsági ellenőrzéssel!');
        setLoading(false);
        return;
      }

      // 4. Válasz kezelése
      if (result.success) {
        //setMessage(result.message);
        setRelatedNicknames(result.relatedNicknames || []);
        setHasSearched(true);
        onSearch?.(result.data); // Szülő tájékoztatása
      } else {
        setError(result.message || 'Hiba történt a művelet során.');
      }

    } catch (err) {
      setError('Szerverkapcsolati hiba.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="search-form">
      <div className="form-header-intro">
        <h1 className="brand-title">AUTÓKERESZTELŐ.HU</h1>
        <h2>Mert minden autónak jár egy név!</h2>
        <h3>Add meg a rendszámot és megmutatjuk, milyen becenév illik hozzá! Te leszel a keresztapja!</h3>
      </div>
      <div className="form-group">

        <LicensePlateInput
          plateNumber={plateNumber}
          setPlateNumber={setPlateNumber}
          nickname={nickname}
          setNickname={setNickname}
          format={format}
          isTaxi={isTaxi}
          setIsTaxi={setIsTaxi}
          isElectric={isElectric}
          setIsElectric={setIsElectric}
        />
        <hr />
        <div className="input-group">
          <label htmlFor="userEmail">
            E-mail címed:

          </label>
          <input
            type="email"
            id="userEmail"
            name="email"
            placeholder="valami@pelda.hu"
          />
          <div className="input-info-note">
            <span className="info-icon" aria-hidden="true">ℹ️</span>
            <p>
              Cserkész becsszó, hogy nem fogunk spammelni! Csak a neked érdekes ajánlatokat küldjük el havonta egyszer.
            </p>
          </div>
        </div>
        <GdprCheckbox gdprChecked={gdprChecked} setGdprChecked={setGdprChecked} />

        <div className="form-group">
          <label>Rendszám formátum:</label>
          <LicensePlateSelector
            selected={format?.id}
            onSelect={handleFormatChange}
          />
        </div>

      </div>

      {/* V2 FALLBACK DOBOZ - Csak akkor jelenik meg, ha a v3 elhasal vagy a backend kéri */}
      {showV2Fallback && (
        <div className="recaptcha-v2-container" style={{ margin: '15px 0', display: 'flex', justifyContent: 'center' }}>
          <ReCAPTCHA
            sitekey={RECAPTCHA_SITE_KEY_V2}
            onChange={(token) => setV2Token(token)}
            onExpired={() => setV2Token(null)}
          />
        </div>
      )}

      {error && <div className="error-message">{error}</div>}
      {message && <div className="info-message">{message}</div>}

      {hasSearched && <SearchResults results={relatedNicknames} />}

      <button type="submit" className="search-button" disabled={!gdprChecked || loading}>
        {loading ? 'Feldolgozás...' : buttonLabel}
      </button>
    </form>
  );
};

export default SearchForm;
